// Live view of the training documents in the download.dstapp.com S3 bucket.
// The bucket allows anonymous listing, so the browser can enumerate
// training/ directly — anything uploaded there appears here with no code
// change. Users are sent mv.dstapp.com links: the DST Media Viewer wraps the
// same bucket path and handles the password-protected PDFs.
const S3_LIST_URL = 'https://s3.us-west-2.amazonaws.com/download.dstapp.com';
const TRAINING_PREFIX = 'training/';
const VIEWER_BASE_URL = 'https://mv.dstapp.com/training/';

// Curated titles/descriptions for the known documents (from the training-docs
// inventory). This map only decorates the live bucket listing — files missing
// from it still show up, auto-titled from their filename, so the bucket stays
// the single source of truth.
const CURATED_DOCS = {
  'DST+Training+Guide.pdf': {
    title: 'DST Ultrasound Diagnostic System Training Manual',
    description: 'The complete DST training manual.',
  },
  'MAIN_TABS_AND_TRAINING_ICONS.pdf': {
    title: 'Main Tabs & Training Icons',
    description: 'What each main tab and training icon does.',
  },
  'DOWNLOAD_&_INSTALL.pdf': {
    title: 'Installing or Redownloading',
    description: 'Install or reinstall the DST mobile app.',
  },
  'CONNECTING_&_SYNCING_DSTAPP_TO_MSi150.pdf': {
    title: 'Connect + Sync DSTAPP to MSi150',
    description: 'Pair the mobile app with an MSi150 and sync inspection data.',
  },
  'Uploading_Files_From_MSi150.pdf': {
    title: 'Upload Files from MSi150',
    description: 'Move recorded files off the MSi150 and into the app.',
  },
  'Storage,_Handling_&_Charging.pdf': {
    title: 'Storage, Handling & Charging',
    description: 'Care guidelines for storing, handling, and charging DST hardware.',
  },
  'PAIRING_HEADSETS.pdf': {
    title: 'Pairing Headsets',
    description: 'Pair Bluetooth headsets with the MSi150.',
  },
  'Updating_Firmware.pdf': {
    title: 'Updating Firmware',
    description: 'Update MSi150 firmware from the app.',
  },
  'Navigation_&_Notifications.pdf': {
    title: 'Navigation, Trackers & Notifications',
    description: 'Get around the app: tabs, trackers, and notification basics.',
  },
  'RESET_INSPECTION_TRACKER.pdf': {
    title: 'Reset Date Range',
    description: "Change or reset the inspection tracker's date range.",
  },
  'DOWNLOAD_A_PROPERTY.pdf': {
    title: 'Downloading a Property',
    description: 'Download a property to the device for offline work.',
  },
  'PROPERTIES.pdf': {
    title: 'Properties',
    description: 'Create and manage properties.',
  },
  'favorite_guide.pdf': {
    title: 'Favorites Guide',
    description: 'Use favorites to keep key properties at hand.',
  },
  'EQUIPMENT.pdf': {
    title: 'Equipment',
    description: 'Add and configure equipment on a property.',
  },
  'dst_nameplate_photo_capture.pdf': {
    title: 'Nameplate Photo Capture',
    description: 'Capture equipment nameplate photos.',
  },
  'SYSTEM_TYPES.pdf': {
    title: 'System Types',
    description: 'Reference for the available equipment system types.',
  },
  'EQUIP_DUPLICATION_PROCEDURE_DSTAPPv2.pdf': {
    title: 'Equipment Duplication',
    description: 'Duplicate equipment to speed up property setup.',
  },
  'ROUTINES.pdf': {
    title: 'Routines',
    description: 'Create routines to scope inspections and reports to specific equipment.',
  },
  'TAKING_INSPECTIONS.pdf': {
    title: 'Taking Inspections',
    description: 'Step-by-step guide to performing an inspection.',
  },
  'CURRENT_INSPECTION.pdf': {
    title: 'Current Inspection',
    description: 'Track progress through the current inspection.',
  },
  'TASKING_SHEETS.pdf': {
    title: 'Tasking Sheets',
    description: 'Assign and complete tasking sheets.',
  },
  'SoundEnergyTM_(SE)_Discovery_Mode.pdf': {
    title: 'SoundEnergy™ (SE) Discovery Mode',
    description: 'Using SE Discovery Mode to capture sound energy readings.',
  },
  'SOUNDSIGNATURETM_DEFINITIONS.pdf': {
    title: 'SoundSignature™ Definitions',
    description: 'Definitions for SoundSignature™ classifications.',
  },
  'ANALYSIS.pdf': {
    title: 'Analysis',
    description: 'Review and analyze inspection results.',
  },
  'EVALUATIONv74.pdf': {
    title: 'Evaluations',
    description: 'How equipment evaluations work.',
  },
  'REPORTS.pdf': {
    title: 'Reports',
    description: 'Generate and send reports from the app.',
  },
  'GLOSSARY.pdf': {
    title: 'Glossary',
    description: 'Glossary of DST terms.',
  },
};

const CURATED_ORDER = Object.keys(CURATED_DOCS);

// Training VIDEOS live in a second bucket (dst-training-media, files at the
// bucket root) and are served through the Media Viewer's generic route, which
// has a built-in video player. This map replicates the mobile app's Training
// page list (mobile/dst/screens/settings/training/VideoList.js) — same
// titles, same categories — and, like CURATED_DOCS, it only decorates the
// live bucket listing: files missing from it still appear, auto-titled.
const VIDEO_LIST_URL = 'https://s3.us-west-2.amazonaws.com/dst-training-media';
const VIDEO_VIEWER_BASE_URL = 'https://mv.dstapp.com/dst-training-media/';
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|avi|wmv|flv)$/i;

const CURATED_VIDEOS = {
  'intro_video_v3_0_2.mp4': { title: 'Intro to DSTAPP', category: 'GENERAL' },
  'connect_sync_dstmobile_to_msi150.mp4': { title: 'Connect + Sync DST Mobile to MSi150', category: 'GENERAL' },
  'uploading_files_from_msi150.mp4': { title: 'Uploading Files From MSi150', category: 'GENERAL' },
  'reset_inspection_trackers_on_msi150_dstmobile.mp4': { title: 'Reset Inspection Trackers on MSi150 & DST Mobile', category: 'GENERAL' },
  'using_dstapp_refresh_function.mp4': { title: "Using DSTAPP's REFRESH Function", category: 'GENERAL' },
  'why_equipment_does_not_appear_on_msi150_after_syncing.mp4': { title: 'Why Equipment Does Not Appear on MSi150 After Syncing', category: 'GENERAL' },
  'storage_handling.mp4': { title: 'Storage & Handling', category: 'MSi150' },
  'navigation_trackers_notification.mp4': { title: 'Navigation, Trackers & Notification', category: 'MSi150' },
  'soundenergy_discovery_mode.mp4': { title: 'SoundEnergy™ (SE) Discovery Mode', category: 'MSi150' },
  'pairing_headsets.mp4': { title: 'Pairing Headsets', category: 'MSi150' },
  'updating_firmware.mp4': { title: 'Updating Firmware', category: 'MSi150' },
  'main_tabs_training_icons.mp4': { title: 'Main Tabs & Training Icons', category: 'DSTAPP' },
  'downloadiing_a_property.mp4': { title: 'Downloading a Property', category: 'DSTAPP' },
  'properties_setting_up_editing.mp4': { title: 'Properties - Setting up & Editing', category: 'DSTAPP' },
  'equipment_setting_up_editing.mp4': { title: 'Equipment - Setting up & Editing', category: 'DSTAPP' },
  'nameplate_photo_capture.mp4': { title: 'Nameplate Photo Capture', category: 'DSTAPP' },
  'system_types.mp4': { title: 'System Types', category: 'DSTAPP' },
  'routines_how_to_set_up_edit_existing_and_benefits.mp4': { title: 'Routines - How to set up, edit existing and benefits', category: 'DSTAPP' },
  'RUNNING_MPe_AND_RUNNING_REPORT.mp4': { title: 'Running MPe and Running Report', category: 'DSTAPP' },
  'current_inspection.mp4': { title: 'Current Inspection', category: 'DSTAPP' },
  'tasking_sheets_adding_to_equipment.mp4': { title: 'Tasking Sheets - Adding To Equipment', category: 'DSTAPP' },
  'tasking_sheets_inputing_data.mp4': { title: 'Tasking Sheets - Inputting Data', category: 'DSTAPP' },
  'reports_how_to_generate_types_of_reports.mp4': { title: 'Reports - How to generate & types of reports', category: 'DSTAPP' },
  'installing_or_updating_dst_mobile.mp4': { title: 'Installing or Updating DST Mobile', category: 'DSTAPP' },
  'navigating_analysis.mp4': { title: 'Navigating Analysis', category: 'ANALYSIS' },
  'how_to_use_dst_to_diagnose_equipment_health.mp4': { title: 'How to use DST to diagnose equipment health', category: 'ANALYSIS' },
  'sound_signaturetm_definitions.mp4': { title: 'Sound Signature™ Definitions', category: 'ANALYSIS' },
  'creating_saving_equipment_evaluations.mp4': { title: 'Creating & Saving Equipment Evaluations', category: 'ANALYSIS' },
  'updating_evaluation_ratings.mp4': { title: 'Updating Evaluation Ratings', category: 'ANALYSIS' },
  'motor-constant_run_or_vfd.mp4': { title: 'Motor - Constant Run or VFD', category: 'TAKING INSPECTIONS' },
  'vfd_controller.mp4': { title: 'VFD Controller', category: 'TAKING INSPECTIONS' },
  'scroll_recip_hermetic_compressor.mp4': { title: 'Scroll/Recip Hermetic Compressor', category: 'TAKING INSPECTIONS' },
  'condenser_fan.mp4': { title: 'Condenser Fan', category: 'TAKING INSPECTIONS' },
  'water_pump.mp4': { title: 'Water Pump', category: 'TAKING INSPECTIONS' },
  'shc_compressor-copeland_discus.mp4': { title: 'SHC Compressor - Copeland Discus', category: 'TAKING INSPECTIONS' },
  'shc_compressor-carlyle_bitzer.mp4': { title: 'SHC Compressor - Carlyle & Bitzer', category: 'TAKING INSPECTIONS' },
  'compressor-screw.mp4': { title: 'Compressor - Screw', category: 'TAKING INSPECTIONS' },
  'compressor-overhung.mp4': { title: 'Compressor - Overhung', category: 'TAKING INSPECTIONS' },
  'compressor-centrifigual.mp4': { title: 'Compressor - Centrifigual', category: 'TAKING INSPECTIONS' },
  'rtu-large_roof_top_unit.mp4': { title: 'RTU - Large Roof Top Unit', category: 'EQUIPMENT - SET UP' },
  'package_unit.mp4': { title: 'Package Unit', category: 'EQUIPMENT - SET UP' },
  'ahu_rtu_ex_fan_assembly.mp4': { title: 'AHU/RTU/EX Fan Assembly', category: 'EQUIPMENT - SET UP' },
  'hermetic_compressor_bank_scroll_recip.mp4': { title: 'Hermetic Compressor Bank (Scroll & Recip)', category: 'EQUIPMENT - SET UP' },
  'condenser_fan_bank.mp4': { title: 'Condenser Fan Bank', category: 'EQUIPMENT - SET UP' },
  'water_pump_system.mp4': { title: 'Water Pump Systems', category: 'EQUIPMENT - SET UP' },
  'boiler.mp4': { title: 'Boiler', category: 'EQUIPMENT - SET UP' },
  'cooling_tower.mp4': { title: 'Cooling Tower', category: 'EQUIPMENT - SET UP' },
  'shc_compressor.mp4': { title: 'SHC Compressor', category: 'EQUIPMENT - SET UP' },
  'rack_compressor.mp4': { title: 'Rack Compressor', category: 'EQUIPMENT - SET UP' },
  'chillers.mp4': { title: 'Chillers', category: 'EQUIPMENT - SET UP' },
};

const CURATED_VIDEO_ORDER = Object.keys(CURATED_VIDEOS);

// Category display order = first appearance in the curated list (mirrors mobile)
export const VIDEO_CATEGORY_ORDER = [...new Set(Object.values(CURATED_VIDEOS).map(video => video.category))];

export default class TrainingLibrary {
  static viewerUrl(filename) {
    return `${VIEWER_BASE_URL}${encodeURIComponent(filename)}`;
  }

  static videoViewerUrl(filename) {
    return `${VIDEO_VIEWER_BASE_URL}${encodeURIComponent(filename)}`;
  }

  // "EQUIP_DUPLICATION_PROCEDURE_DSTAPPv2.pdf" -> "Equip Duplication Procedure Dstappv2"
  static titleFromFilename(filename) {
    const base = filename.replace(/\.pdf$/i, '').replace(/[_+]/g, ' ').trim();
    return base
      .split(/\s+/)
      .map(word => (/^[A-Z0-9&(),.'-]+$/.test(word)
        ? word.charAt(0) + word.slice(1).toLowerCase()
        : word))
      .join(' ');
  }

  // Anonymous, paginated ListObjectsV2 over a public bucket; returns
  // [{filename, lastModified, size}] for keys under the prefix.
  static listBucket = async (listUrl, prefix) => {
    const files = [];
    let continuationToken = null;
    do {
      const tokenParam = continuationToken
        ? `&continuation-token=${encodeURIComponent(continuationToken)}`
        : '';
      const res = await fetch(`${listUrl}?list-type=2&prefix=${prefix}${tokenParam}`);
      if (!res.ok) {
        throw new Error(`Training library listing failed (${res.status})`);
      }
      const xml = new DOMParser().parseFromString(await res.text(), 'application/xml');
      if (xml.querySelector('parsererror')) {
        throw new Error('Training library listing returned unreadable XML');
      }
      xml.querySelectorAll('Contents').forEach(node => {
        const key = node.querySelector('Key')?.textContent || '';
        const filename = key.slice(prefix.length);
        if (!filename) return; // the folder key itself
        files.push({
          filename,
          lastModified: node.querySelector('LastModified')?.textContent || null,
          size: parseInt(node.querySelector('Size')?.textContent || '0', 10),
        });
      });
      const truncated = xml.querySelector('IsTruncated')?.textContent === 'true';
      continuationToken = truncated
        ? xml.querySelector('NextContinuationToken')?.textContent
        : null;
    } while (continuationToken);
    return files;
  };

  // Training documents (PDFs in download.dstapp.com/training/):
  // [{filename, title, description, url, lastModified, size, curated}],
  // curated docs first in inventory order, the rest alphabetical.
  static fetchDocs = async () => {
    const files = (await this.listBucket(S3_LIST_URL, TRAINING_PREFIX))
      .filter(file => /\.pdf$/i.test(file.filename));

    const docs = files.map(file => {
      const curated = CURATED_DOCS[file.filename];
      return {
        ...file,
        curated: !!curated,
        title: curated ? curated.title : this.titleFromFilename(file.filename),
        description: curated ? curated.description : null,
        url: this.viewerUrl(file.filename),
      };
    });

    return docs.sort((a, b) => {
      if (a.curated !== b.curated) return a.curated ? -1 : 1;
      if (a.curated && b.curated) {
        return CURATED_ORDER.indexOf(a.filename) - CURATED_ORDER.indexOf(b.filename);
      }
      return a.title.localeCompare(b.title);
    });
  };

  // Training videos (dst-training-media bucket root):
  // [{filename, title, category, url, lastModified, size, curated}],
  // curated videos first in the mobile list's order (grouped by category by
  // the caller), the rest alphabetical with category null.
  static fetchVideos = async () => {
    const files = (await this.listBucket(VIDEO_LIST_URL, ''))
      .filter(file => VIDEO_EXTENSIONS.test(file.filename));

    const videos = files.map(file => {
      const curated = CURATED_VIDEOS[file.filename];
      return {
        ...file,
        curated: !!curated,
        title: curated ? curated.title : this.titleFromFilename(file.filename.replace(VIDEO_EXTENSIONS, '')),
        category: curated ? curated.category : null,
        description: null,
        url: this.videoViewerUrl(file.filename),
      };
    });

    return videos.sort((a, b) => {
      if (a.curated !== b.curated) return a.curated ? -1 : 1;
      if (a.curated && b.curated) {
        return CURATED_VIDEO_ORDER.indexOf(a.filename) - CURATED_VIDEO_ORDER.indexOf(b.filename);
      }
      return a.title.localeCompare(b.title);
    });
  };
}
